/**
 * SIP Manipulations Hooks - React Query hooks for SIP Manipulation profiles
 * 
 * Uses Sip.ManipulationInfo types from @sbclite/core for type safety
 * Following SST Console pattern from CORE_SST_PATTERN_IMPLEMENTATION.md
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Sip } from '@sbclite/core';
import { api } from '../services/api';

/**
 * API Response types
 */
interface ManipulationsResponse {
  success: boolean;
  data: Sip.ManipulationInfo[];
}

interface ManipulationResponse {
  success: boolean;
  data: Sip.ManipulationInfo;
}

interface ManipulationDropdownResponse {
  success: boolean;
  data: Array<{
    id: string;
    name: string;
    description: string | null;
    enabled: boolean;
    ruleCount: number;
  }>;
}

interface ManipulationUsageResponse {
  success: boolean;
  data: {
    inUse: boolean;
    peerCount: number;
    ruleCount: number;
  };
}

interface ManipulationTestResponse {
  success: boolean;
  data: {
    original: {
      from: string;
      to: string;
      requestUri?: string;
      pAssertedIdentity?: string;
      remotePartyId?: string;
      contact?: string;
      via?: string;
    };
    transformed: {
      from: string;
      to: string;
      requestUri?: string;
      pAssertedIdentity?: string;
      remotePartyId?: string;
      contact?: string;
      via?: string;
    };
    appliedRules: Array<{
      priority: number;
      target: string;
      matched: boolean;
      before?: string;
      after?: string;
      reason?: string;
    }>;
  };
}

/**
 * Fetch all SIP manipulation profiles
 */
export function useSipManipulations() {
  return useQuery({
    queryKey: ['sip-manipulations'],
    queryFn: async (): Promise<ManipulationsResponse> => {
      const response = await api.get<ManipulationsResponse>('/api/sip-manipulations');
      return response.data as ManipulationsResponse;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Fetch single SIP manipulation profile by ID
 */
export function useSipManipulation(id: string) {
  return useQuery<ManipulationResponse>({
    queryKey: ['sip-manipulation', id],
    queryFn: async () => {
      const response = await api.get<ManipulationResponse>(`/api/sip-manipulations/${id}`);
      return response.data as ManipulationResponse;
    },
    enabled: !!id,
  });
}

/**
 * Fetch manipulation profiles for dropdown selection
 */
export function useSipManipulationsForDropdown() {
  return useQuery({
    queryKey: ['sip-manipulations-dropdown'],
    queryFn: async (): Promise<ManipulationDropdownResponse> => {
      const response = await api.get<ManipulationDropdownResponse>('/api/sip-manipulations/dropdown');
      return response.data as ManipulationDropdownResponse;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Check if a manipulation profile is in use
 */
export function useSipManipulationUsage(id: string) {
  return useQuery<ManipulationUsageResponse>({
    queryKey: ['sip-manipulation-usage', id],
    queryFn: async () => {
      const response = await api.get<ManipulationUsageResponse>(`/api/sip-manipulations/${id}/usage`);
      return response.data as ManipulationUsageResponse;
    },
    enabled: !!id,
  });
}

/**
 * Create SIP manipulation profile mutation
 */
export function useCreateSipManipulation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      description?: string;
      enabled?: boolean;
      rules?: Sip.ManipulationRule[];
    }) => {
      const response = await api.post<ManipulationResponse>('/api/sip-manipulations', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-manipulations'] });
      queryClient.invalidateQueries({ queryKey: ['sip-manipulations-dropdown'] });
    },
  });
}

/**
 * Update SIP manipulation profile mutation
 */
export function useUpdateSipManipulation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: {
      id: string;
      data: {
        name?: string;
        description?: string | null;
        enabled?: boolean;
        rules?: Sip.ManipulationRule[];
      };
    }) => {
      const response = await api.put<ManipulationResponse>(`/api/sip-manipulations/${id}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sip-manipulations'] });
      queryClient.invalidateQueries({ queryKey: ['sip-manipulation', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['sip-manipulations-dropdown'] });
    },
  });
}

/**
 * Delete SIP manipulation profile mutation
 */
export function useDeleteSipManipulation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/sip-manipulations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-manipulations'] });
      queryClient.invalidateQueries({ queryKey: ['sip-manipulations-dropdown'] });
    },
  });
}

/**
 * Test SIP manipulation against a message
 */
export function useTestSipManipulation() {
  return useMutation({
    mutationFn: async (data: {
      manipulationId?: string;
      rules?: Sip.ManipulationRule[];
      message: {
        from: string;
        to: string;
        requestUri?: string;
        pAssertedIdentity?: string;
        remotePartyId?: string;
        contact?: string;
        via?: string;
      };
    }) => {
      const response = await api.post<ManipulationTestResponse>('/api/sip-manipulations/test', data);
      return response.data;
    },
  });
}

/**
 * Toggle SIP manipulation enabled state
 */
export function useToggleSipManipulation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const response = await api.put<ManipulationResponse>(`/api/sip-manipulations/${id}`, { enabled });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sip-manipulations'] });
      queryClient.invalidateQueries({ queryKey: ['sip-manipulation', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['sip-manipulations-dropdown'] });
    },
  });
}
